// File: src/components/layout/MainLayout.tsx
import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  Home,
  Video,
  Calendar,
  Settings,
  BarChart3,
  Bell,
  User,
  ChevronDown,
  Moon,
  Sun,
  Zap,
  Crown,
  Megaphone,
  CheckCircle,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useAppStore } from '@/stores/appStore';
import { useFeatureAccess } from '@/components/auth/ProtectedRoute';
import { Button } from '@/components/ui/Button';
import { NotificationPanel } from '@/components/notifications/NotificationPanel';
import { UserMenu } from '@/components/layout/UserMenu';
import { ChannelSelector } from '@/components/channels/ChannelSelector';

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  feature?: string;
  badge?: string;
  pro?: boolean;
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Projects', href: '/projects', icon: Video },
  { name: 'Approve Videos', href: '/video-approval', icon: CheckCircle },
  { name: 'Scheduling', href: '/scheduling', icon: Calendar, feature: 'auto_scheduling', pro: true },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, feature: 'analytics', pro: true },
  { name: 'Channels', href: '/channels', icon: Megaphone },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export const MainLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const { userProfile,
    // signOut 
  } = useAuthStore();

  const {
    theme,
    sidebarCollapsed,
    toggleTheme,
    toggleSidebar,
    notifications,
    channels,
    selectedChannel,
    setSelectedChannel,
    fetchChannels,
  } = useAppStore();

  const { hasFeature, subscription } = useFeatureAccess();

  useEffect(() => {
    if (channels.length === 0) {
      fetchChannels().catch(console.error);
    }
  }, [channels.length, fetchChannels]);
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowNotifications(false);
      setShowUserMenu(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  //  const handleSignOut = async () => {
  //     try {
  //       await signOut();
  //       navigate('/auth/signin');
  //     } catch (error) {
  //       console.error('Sign out failed:', error);
  //     }
  //   };

  const handleChannelSelect = (channelId: string) => {
    setSelectedChannel(channelId);
  };

  const handleAddChannel = () => {
    navigate('/channels?action=connect');
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;
  const isFreeTier = subscription?.tier === 'free';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Sidebar */}
      <aside
        className={`${sidebarCollapsed ? 'w-18' : 'w-64'
          } bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 relative z-10`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          {!sidebarCollapsed && (
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900 dark:text-white">
                TubeOps
              </span>
            </Link>
          )}

          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="p-2"
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>

        {/* Channel Selector */}
        {!sidebarCollapsed && (
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <ChannelSelector
              channels={channels}
              selectedChannel={selectedChannel}
              onSelectChannel={handleChannelSelect}
              onAddChannel={handleAddChannel}
            />
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const canAccess = !item.feature || hasFeature(item.feature);
            const isLocked = item.pro && !canAccess;

            return (
              <div key={item.name} className="relative">
                <Link
                  to={isLocked ? '/upgrade' : item.href}
                  className={`
                    flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${isActive
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                      : isLocked
                        ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                  `}
                  onClick={(e) => {
                    if (isLocked) {
                      e.preventDefault();
                      navigate('/upgrade');
                    }
                  }}
                >

                  <item.icon
                    className={`${sidebarCollapsed ? 'w-8 h-8 mx-auto' : 'w-5 h-5'} transition-all duration-300`}
                  />                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1">{item.name}</span>
                      {isLocked && <Crown className="w-4 h-4 text-yellow-500" />}
                    </>
                  )}
                </Link>
              </div>
            );
          })}
        </nav>

        {/* User Profile Section */}
        <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
          {sidebarCollapsed ? (
            <Button
              variant="ghost"
              size="sm"
              className="w-full p-2"
              onClick={() => setShowUserMenu(true)}
            >
              <User className="w-5 h-5" />
            </Button>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {userProfile?.name?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {userProfile?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {subscription?.tier || 'Free'} Plan
                  </p>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="relative"
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Upgrade CTA for Free Users */}
        {!sidebarCollapsed && isFreeTier && (
          <div className="px-4 pb-4">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-3 text-white">
              <div className="flex items-center space-x-2 mb-2">
                <Crown className="w-5 h-5" />
                <span className="font-medium">Upgrade to Pro</span>
              </div>
              <p className="text-xs opacity-90 mb-3">
                Unlock unlimited videos and advanced features
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/upgrade')}
                className="w-full bg-white text-purple-600 hover:bg-gray-100"
              >
                Upgrade Now
              </Button>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 h-16 flex items-center justify-between px-6">
          <div className="flex items-center space-x-4">
            {/* Page title would go here */}
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {navigation.find(item => item.href === location.pathname)?.name || 'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="p-2"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5" />
              ) : (
                <Sun className="w-5 h-5" />
              )}
            </Button>

            {/* Notifications */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNotifications(!showNotifications);
                }}
                className="p-2 relative"
              >
                <Bell className="w-5 h-5" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </Button>

              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 top-full mt-2 w-80 z-50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <NotificationPanel onClose={() => setShowNotifications(false)} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* User Menu */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUserMenu(!showUserMenu);
                }}
                className="flex items-center space-x-2 p-2"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {userProfile?.name?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4" />
              </Button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 top-full mt-2 z-50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <UserMenu onClose={() => setShowUserMenu(false)} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;